import React, { useState, useRef, useEffect, useMemo } from 'react'
import '../views/FriterieSection.css'
import '../style.css'
import { useCart } from '../context/CartContext'
import ItemOptionsModal from './ItemOptionsModal'
import SAUCES from '../utils/sauces'
import VIANDES_MITRAILLETTE from '../utils/viandes'
import { getOpeningStatus, DAY_NAMES_SHORT, DEFAULT_OPEN_DAYS, DEFAULT_ONLINE_ORDER_DAYS, DAY_NAMES } from '../utils/openingHours'
import { parsePrice, formatPrice } from '../utils/parsePrice'
const API_BASE = process.env.REACT_APP_API_URL ?? ''

// Sections where the isMeatOption flag is relevant for items
const MEAT_OPTION_SECTIONS = ['snacks', 'brochettes_poulet_poisson', 'burgers']

const EMPTY_NEW_ITEM = {
  name: '',
  price: '',
  subtitle: '',
  chooseMeat: false,
  chooseSauce: false,
  chooseSupplements: false,
  chooseBrochette: false,
  isMeatOption: false,
  outOfStock: false,
  image: '',
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Compute smart default option flags for a new item being added to a group.
 * - In the 'burgers' section sauce and supplements are always shown at the
 *   section level, so pre-check them so the stored flags stay consistent.
 * - For all other sections, look at the existing items in the same group: if
 *   the majority already have a given flag set, default the new item to the
 *   same value to avoid confusion.
 */
function getGroupItemDefaults(group, sectionId) {
  if (sectionId === 'burgers') {
    return { chooseSauce: true, chooseSupplements: true }
  }
  const items = group?.items || []
  if (items.length === 0) return {}
  const half = items.length / 2
  const counts = items.reduce(
    (acc, i) => {
      if (i.chooseSauce) acc.sauce++
      if (i.chooseMeat) acc.meat++
      if (i.chooseSupplements) acc.Suppléments++
      if (i.isMeatOption) acc.isMeatOption++
      return acc
    },
    { sauce: 0, meat: 0, Suppléments: 0, isMeatOption: 0 }
  )
  return {
    chooseSauce: counts.sauce > half,
    chooseMeat: counts.meat > half,
    chooseSupplements: counts.Suppléments > half,
    isMeatOption: counts.isMeatOption > half,
  }
}

/**
 * Convert a server-relative image path (e.g. "/img/uploads/burger-123.jpg")
 * to an absolute URL using API_BASE so that images uploaded to the Railway
 * backend are reachable even when the frontend is hosted on a different domain
 * (e.g. Hostinger).  Absolute URLs and null/undefined are returned unchanged.
 */
function resolveImageUrl(url) {
  if (!url) return url

  // Images uploadées sur Railway (retournées par l'API)
  if (url.startsWith('/img/uploads/')) {
    return `${API_BASE}${url}`
  }

  // Images statiques du frontend (Hostinger) ou URL absolue (https://...)
  return url
}

/**
 * Compress an image File using the Canvas API before uploading.
 * Resizes to at most maxDim × maxDim pixels and re-encodes as JPEG at the
 * given quality (0–1).  Returns a new File object so it can be sent via
 * FormData just like the original.
 */
async function compressImage(file, { maxDim = 1200, quality = 0.82 } = {}) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const objectUrl = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(objectUrl)
      let { width, height } = img
      if (width > maxDim || height > maxDim) {
        if (width >= height) {
          height = Math.round((height * maxDim) / width)
          width = maxDim
        } else {
          width = Math.round((width * maxDim) / height)
          height = maxDim
        }
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)
      canvas.toBlob(
        (blob) => {
          if (!blob) return reject(new Error('Canvas compression failed'))
          resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' }))
        },
        'image/jpeg',
        quality
      )
    }
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error('Image load failed'))
    }
    img.src = objectUrl
  })
}

// ─── Card group (burgers spéciaux & assiettes) ────────────────────────────────

const CardGroup = ({ group, sectionId, gIndex, onAdd, isOpen }) => (
  <div className="friterie-card-grid">
    {group.items?.map((item, iIndex) => (
      <div key={iIndex} className={`friterie-card-item${item.outOfStock ? ' friterie-card-item--out-of-stock' : ''}`}>
        <div className="friterie-card-media">
          <img
            src={resolveImageUrl(item.image) || '/img/placeholder-burger.svg'}
            alt={item.name}
            className="friterie-card-img"
            onError={(e) => {
              e.target.src = '/img/placeholder-burger.svg'
            }}
          />
        </div>
        <div className="friterie-card-body">
          <h5 className="friterie-card-name">{item.name}</h5>
          {item.subtitle && (
            <p className="friterie-card-desc">{item.subtitle}</p>
          )}
          <div className="friterie-card-footer">
            <span className="friterie-card-price">{item.priceLabel} </span>
            {item.outOfStock ? (
              <span className="friterie-out-of-stock-badge">Rupture de stock</span>
            ) : (
              <button
                type="button"
                className="friterie-add-button friterie-add-button--primary"
                disabled={!isOpen}
                onClick={() => onAdd(item, sectionId, gIndex, iIndex)}
              >
                Ajouter
              </button>
            )}
          </div>
        </div>
      </div>
    ))}
  </div>
)

// ─── Main component ────────────────────────────────────────────────────────────

const MenuDisplay = ({
  menu,
  activeTab,
  setActiveTab,
  mode = 'view',
  onChangeBurger,
  onChangeSectionField,
  onChangeItem,
  onUpdateItem,
  onDeleteItem,
  onAddItem,
  onChangeSauceList,
  onChangeAnnouncement,
  onChangeOpenDays,
  onLogout,
  handleSaveClick,
  onSaveClick,
  saveState,
  logoutConfirm,
}) => {
  const { addItem } = useCart()
  const { burgerDuMois, sections, sauces: saucesFromMenu, announcement, openDays, onlineOrderDays, openingHours, onlineOrderHours } = menu || {};
  const sauceList = saucesFromMenu || SAUCES
  const applyToAllDays = (hours) => {
    const next = {}

    DAY_NAMES_SHORT.forEach((_, i) => {
      next[i] = {
        open: hours.open,
        close: hours.close
      }
    })

    onChangeOpenDays('openingHours', next)
  }
  // Compute viande list dynamically from items with isMeatOption: true.
  // Deduplicated by name to avoid duplicates when the same item appears in multiple sections.
  const viandeList = useMemo(() => {
    if (!sections) return VIANDES_MITRAILLETTE
    const seen = new Set()
    const result = []
    for (const section of sections) {
      for (const group of section.groups || []) {
        for (const item of group.items || []) {
          if (item.isMeatOption && !seen.has(item.name)) {
            seen.add(item.name)
            result.push({ name: item.name, price: parsePrice(item.price) })
          }
        }
      }
    }
    return result.length > 0 ? result : VIANDES_MITRAILLETTE
  }, [sections])

  // Compute brochette list from the "Brochettes" group in the brochettes_poulet_poisson section.
  const brochetteList = useMemo(() => {
    if (!sections) return []
    const section = sections.find((s) => s.id === 'brochettes_poulet_poisson')
    if (!section) return []
    const group = section.groups?.find((g) => g.title === 'Brochettes')
    if (!group) return []
    return group.items
      .filter((item) => !item.outOfStock)
      .map((item) => ({ name: item.name, price: parsePrice(item.price) }))
  }, [sections])

  // ── Opening status ───────────────────────────────────────────────────────────
  const effectiveOpenDays = openDays ?? DEFAULT_OPEN_DAYS
  const effectiveOnlineOrderDays = onlineOrderDays ?? DEFAULT_ONLINE_ORDER_DAYS
  const {
    isOnlineOrderOpen: canOrderOnline,
    detail: openingDetail,
    times: openingTimes
  } = getOpeningStatus(new Date(), {
    openDays,
    onlineOrderDays,
    openingHours,
    onlineOrderHours
  })
  const announcementActive = mode === 'view' && !!(announcement?.active && announcement?.message)
  // When an announcement is active the store is considered closed for ordering
  const effectivelyOpen = canOrderOnline && !announcementActive

  // ── Modal state ──────────────────────────────────────────────────────────────
  const [modalItem, setModalItem] = useState(null)

  // ── Inline sauce selector state (frites section) ─────────────────────────────
  const [inlineSauces, setInlineSauces] = useState({})

  // ── Inline frites format selector state (en cornet / en ravier) ─────────────
  const [inlineFritesFormats, setInlineFritesFormats] = useState({})

  // ── Admin image upload (burger du mois) ─────────────────────────────────────
  const fileInputRef = useRef(null)
  const [uploadStatus, setUploadStatus] = useState('')

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadStatus('Compression…')

    let fileToUpload = file
    try {
      fileToUpload = await compressImage(file)
    } catch (compressErr) {
      console.warn('[upload] Image compression failed, using original file:', compressErr.message)
      fileToUpload = file
    }
    setUploadStatus('Envoi en cours…')

    const formData = new FormData()
    formData.append('image', fileToUpload)

    try {
      const token = localStorage.getItem('menu_admin_token') || ''
      const res = await fetch(`${API_BASE}/api/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!res.ok) throw new Error('Upload failed')
      const data = await res.json()
      onChangeBurger?.('image', data.url)
      setUploadStatus('Image mise à jour ✓')
    } catch (err) {
      console.error(err)
      setUploadStatus("Erreur lors de l'envoi de l'image")
    }
  }

  // ── Admin image upload (card items – burgers spéciaux & assiettes) ──────────
  const cardItemFileInputRef = useRef(null)
  const [pendingItemUpload, setPendingItemUpload] = useState(null)
  const [cardItemUploadStatus, setCardItemUploadStatus] = useState({})

  // ── Admin add-item form state ────────────────────────────────────────────────
  const [addingInGroup, setAddingInGroup] = useState(null) // { sectionIndex, groupIndex }
  const [newItemData, setNewItemData] = useState({ ...EMPTY_NEW_ITEM })
  const addItemFileInputRef = useRef(null)
  const [addItemImageStatus, setAddItemImageStatus] = useState('')

  // ── Admin sauce list editor state ────────────────────────────────────────────
  const [newSauceName, setNewSauceName] = useState('')
  const [editingSauceIdx, setEditingSauceIdx] = useState(null)

  // ── Admin per-item editing state ─────────────────────────────────────────────
  const [editingItemKey, setEditingItemKey] = useState(null) // `${sectionIdx}-${gIdx}-${iIdx}`
  const [editItemData, setEditItemData] = useState(null)     // local copy of item being edited

  const startEditingItem = (sectionIdx, gIdx, iIdx, item) => {
    setEditingItemKey(`${sectionIdx}-${gIdx}-${iIdx}`)
    setEditItemData({ ...item })
  }

  const handleEditItemConfirm = (sectionIdx, gIdx, iIdx) => {
    if (!editItemData) return
    onUpdateItem?.(sectionIdx, gIdx, iIdx, editItemData)
    setEditingItemKey(null)
    setEditItemData(null)
  }

  const handleEditItemCancel = () => {
    setEditingItemKey(null)
    setEditItemData(null)
  }

  const handleCardItemImageClick = (sectionIndex, groupIndex, itemIndex) => {
    setPendingItemUpload({ sectionIndex, groupIndex, itemIndex })
    cardItemFileInputRef.current?.click()
  }

  const handleCardItemFileChange = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !pendingItemUpload) return
    const { sectionIndex, groupIndex, itemIndex } = pendingItemUpload
    const key = `${sectionIndex}-${groupIndex}-${itemIndex}`
    setCardItemUploadStatus((prev) => ({ ...prev, [key]: 'Compression…' }))

    let fileToUpload = file
    try {
      fileToUpload = await compressImage(file)
    } catch (compressErr) {
      console.warn('[upload] Image compression failed, using original file:', compressErr.message)
      fileToUpload = file
    }
    setCardItemUploadStatus((prev) => ({ ...prev, [key]: 'Envoi…' }))

    const formData = new FormData()
    formData.append('image', fileToUpload)

    try {
      const token = localStorage.getItem('menu_admin_token') || ''
      const res = await fetch(`${API_BASE}/api/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!res.ok) throw new Error('Upload failed')
      const data = await res.json()
      onChangeItem?.(sectionIndex, groupIndex, itemIndex, 'image', data.url)
      // Also update editItemData preview if this item is currently being edited
      const editKey = `${sectionIndex}-${groupIndex}-${itemIndex}`
      if (editingItemKey === editKey) {
        setEditItemData((prev) => prev ? { ...prev, image: data.url } : prev)
      }
      setCardItemUploadStatus((prev) => ({ ...prev, [key]: '✓' }))
    } catch (err) {
      console.error(err)
      setCardItemUploadStatus((prev) => ({ ...prev, [key]: 'Erreur' }))
    }
    e.target.value = ''
    setPendingItemUpload(null)
  }

  // ── Add-item image upload ────────────────────────────────────────────────────
  const handleAddItemImageUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setAddItemImageStatus('Compression…')

    let fileToUpload = file
    try {
      fileToUpload = await compressImage(file)
    } catch {
      fileToUpload = file
    }
    setAddItemImageStatus('Envoi…')

    const formData = new FormData()
    formData.append('image', fileToUpload)

    try {
      const token = localStorage.getItem('menu_admin_token') || ''
      const res = await fetch(`${API_BASE}/api/upload-image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
      if (!res.ok) throw new Error('Upload failed')
      const data = await res.json()
      setNewItemData((prev) => ({ ...prev, image: data.url }))
      setAddItemImageStatus('✓')
    } catch (err) {
      console.error(err)
      setAddItemImageStatus('Erreur')
    }
    e.target.value = ''
  }

  // ── Tabs ─────────────────────────────────────────────────────────────────────
  const tabs =
    sections?.map((section) => ({
      id: section.id,
      label: section.tabLabel || section.label,
    })) || []
  const normalizeItem = (item) => {
    if (!item) return null

    if (typeof item === 'object') {
      return {
        name: item.name,
        price: parsePrice(item.price ?? 0),
      }
    }

    return {
      name: item,
      price: 0,
    }
  }
  const currentTab =
    activeTab || (sections && sections[0] && sections[0].id)
  const activeSection =
    sections?.find((section) => section.id === currentTab) || sections?.[0]

  // Reset add-item form and item editing when changing tabs.
  // If an item edit is in progress, auto-confirm it first so changes are not lost.
  useEffect(() => {
    if (editingItemKey && editItemData && onUpdateItem) {
      const [sectionIdx, gIdx, iIdx] = editingItemKey.split('-').map(Number)
      onUpdateItem(sectionIdx, gIdx, iIdx, editItemData)
    }
    setAddingInGroup(null)
    setNewItemData({ ...EMPTY_NEW_ITEM })
    setAddItemImageStatus('')
    setEditingItemKey(null)
    setEditItemData(null)
  }, [activeTab]) // intentionally only runs on tab change

  // ── Add-to-cart handlers ──────────────────────────────────────────────────────

  const handleAddItem = (item, sectionId, gIndex, iIndex) => {
    if (!effectivelyOpen) return

    const showSauce = !!item.chooseSauce || sectionId === 'burgers'
    const showSupplements = sectionId === 'burgers' || !!item.chooseSupplements
    const showViande = !!item.chooseMeat
    const showBrochette = !!item.chooseBrochette

    let displayName = item.name

    if (sectionId === 'brochettes_poulet_poisson') {
      const section = sections.find((s) => s.id === sectionId)
      const group = section?.groups[gIndex]

      if (group?.title) {
        displayName =
          group.title === 'Brochettes'
            ? `Brochette ${item.name}`
            : `${group.title} – ${item.name}`
      }
    }

    const hasOptions =
      showSauce || showSupplements || showViande || showBrochette

    if (hasOptions) {
      setModalItem({
        item,
        sectionId,
        gIndex,
        iIndex,
        showSauce,
        showSupplements,
        showViande,
        showBrochette,
        displayName,
        sauces: sauceList,
        viandes: viandeList,
        brochettes: brochetteList,
      })
      return
    }

    // 🧾 SIMPLE ITEM (NO MODAL)
    addItem({
      id: `${sectionId}-${gIndex}-${iIndex}`,
      name: item.name,
      displayName,
      price: item.price || '0',
      options: null,
    })
  }
  const handleModalConfirm = (
    sauce,
    Suppléments,
    viande,
    selectedBrochettes,
    choiceType,
    burger
  ) => {
    if (!modalItem) return

    const { item, sectionId, gIndex, iIndex, displayName } = modalItem

    let totalPrice = parsePrice(item.price)
    let finalName = displayName || item.name

    const options = {
      sauce: sauce || null,
      viande: null,
      burger: null,
      brochette: [],
      supplements: [],
      boxEnfant: null,
    }

    const addPrice = (p) => {
      const price = parsePrice(p?.price ?? p ?? 0)
      if (!isNaN(price)) totalPrice += price
    }

    // 🍔 BOX ENFANT
    if (item?.name === 'Box Enfant') {

      const selectedRaw =
        choiceType === 'burger'
          ? burger
          : viande


      const selectedItem = normalizeItem(selectedRaw)

      options.boxEnfant = {
        type: choiceType,
        item: selectedItem,
      }


      addPrice(selectedItem)
    }

    // 🥩 VIANDE (UX + PRICE)
    if (viande && item?.name !== 'Box Enfant') {

      const viandeItem = normalizeItem(viande)

      options.viande = viandeItem

      addPrice(viandeItem)
    }
    if (burger && item?.name !== 'Box Enfant') {

      const burgerItem = normalizeItem(burger)

      options.burger = burgerItem

      addPrice(burgerItem)
    }

    // 🍢 BROCHETTES
    if (selectedBrochettes?.length) {
      const valid = selectedBrochettes.filter(Boolean)

      options.brochette = valid.map((b) => ({
        name: b.name,
        price: b.price,
      }))

      valid.forEach(addPrice)
    }

    // 🍟 SUPPLÉMENTS
    if (Array.isArray(Suppléments) && Suppléments.length > 0) {
      options.supplements = Suppléments.map((s) => ({
        name: s.name,
        price: s.price,
      }))

      Suppléments.forEach(addPrice)
    }

    // 🧾 ID STABLE
    const idParts = [
      sectionId,
      gIndex,
      iIndex,
      options.viande?.name,
      options.burger?.name,
      options.brochette?.map(b => b.name).join('+'),
      options.supplements?.map(s => s.name).join('+'),
      options.boxEnfant?.type,
      options.boxEnfant?.item?.name,
    ].filter(Boolean)

    addItem({
      id: idParts.join('-'),
      name: item.name,
      displayName: finalName,
      price: formatPrice(totalPrice),
      options,
    })

    setModalItem(null)
  }
  const handleInlineSauceAdd = (item, sectionId, gIndex, iIndex) => {
    if (!effectivelyOpen) return

    const key = `${sectionId}-${gIndex}-${iIndex}`
    const selectedSauce = inlineSauces[key] || null

    addItem({
      id: `${sectionId}-${gIndex}-${iIndex}-${selectedSauce || 'nosauce'}`,
      name: item.name,
      price: item.price || '1,00€',
      options: {
        sauce: selectedSauce,
      },
    })
  }

  const handleInlineFritesFormatAdd = (item, sectionId, gIndex, iIndex) => {
    if (!effectivelyOpen) return

    const key = `${sectionId}-${gIndex}-${iIndex}`
    const selectedFormat = inlineFritesFormats[key]
    if (!selectedFormat) return

    addItem({
      id: `${sectionId}-${gIndex}-${iIndex}-${selectedFormat}`,
      name: item.name,
      price: item.price || '',
      options: {
        ...item.options,
        fritesFormat: selectedFormat,
      }
    })
  }

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <section className="friterie-container">
      {/* HERO */}
      <section className="friterie-hero">
        <div className="hero">
          <img src="/img/logo_Lalaing.png" className="hero-logo" alt="Logo" />
        </div>
        <div className="friterie-hero-content">
          {mode === 'edit' && (
            <h1 className="friterie-hero-title">Édition du menu</h1>
          )}

        </div>
        {mode === 'view' && announcementActive && announcement?.heroClosedBadgeActive && (
          <div className="hero-closed-badge" aria-label="Magasin fermé temporairement">
            FERMÉE
          </div>
        )}
        {mode === 'view' && (
          <div className="scroll-indicator" aria-hidden="true">
            <p className="friterie-hero-subtitle">
              Une carte simple, efficace et généreuse, comme une vraie friterie de village.
            </p>
            <span className="scroll-indicator-chevron" />
          </div>

        )}
      </section>

      {/* ANNONCE PONCTUELLE */}
      {mode === 'view' && announcement?.active && announcement?.message && (
        <div className="menu-announcement-banner" role="status" aria-live="polite">
          <span className="menu-announcement-icon">📢</span>
          <div className="menu-announcement-text">
            <strong>Annonce</strong>
            <span>{announcement.message}</span>
          </div>
        </div>
      )}

      {/* NOTICE HORAIRES */}


      {/* ADMIN STICKY BAR */}
      {mode === 'edit' && (
        <div className="admin-sticky-bar">

          <button
            type="button"
            onClick={onSaveClick}
            className="admin-button admin-button--danger"
          >
            {saveState === 'confirm' && 'Confirmer la sauvegarde ?'}
            {saveState === 'saving' && 'Sauvegarde...'}
            {saveState === 'success' && 'Sauvegardé ✓'}
            {saveState === 'error' && '❌ Erreur'}
            {saveState === 'idle' && '💾 Sauvegarder'}
            {saveState === null && '💾 null'}
            {saveState === undefined && '💾 undefined'}


          </button>

          <button
            type="button"
            className="admin-button admin-button--danger"
            onClick={onLogout}
          >
            {logoutConfirm ? 'Confirmer la déconnexion ?' : '🔓 Déconnexion'}
          </button>
        </div>
      )}

      <main className="friterie-main">
        {/* Hidden file inputs for image uploads */}
        {mode === 'edit' && (
          <input
            ref={cardItemFileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleCardItemFileChange}
          />
        )}

        {/* BURGER DU MOIS */}
        {burgerDuMois && (
          <section className="friterie-burger-highlight" aria-label="Burger du mois">
            {mode === 'edit' ? (
              <input
                type="text"
                className="admin-input-title"
                value={burgerDuMois.title}
                onChange={(e) => onChangeBurger?.('title', e.target.value)}
              />
            ) : (

              <h2 className="friterie-burger-title">{burgerDuMois.title}</h2>
            )}

            <div className="friterie-burger-card">
              <div className="friterie-burger-media">
                <img
                  src={resolveImageUrl(burgerDuMois.image)}
                  alt={burgerDuMois.title}
                  className="friterie-burger-image"
                />
              </div>
              <div className="friterie-burger-content">
                {mode === 'edit' && (
                  <div className="admin-fullwidth">
                    <div className="admin-image-upload">

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                      />

                      <button
                        type="button"
                        className="admin-button admin-button--primary"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Choisir une image…
                      </button>

                      {uploadStatus && (
                        <span className="admin-upload-status">
                          {uploadStatus}
                        </span>
                      )}

                    </div>
                  </div>
                )}

                {mode === 'edit' ? (
                  <textarea
                    rows={3}
                    className="admin-fullwidth"
                    value={burgerDuMois.description}
                    onChange={(e) =>
                      onChangeBurger?.('description', e.target.value)
                    }
                  />
                ) : (
                  <p className="friterie-burger-desc">
                    {burgerDuMois.description}
                  </p>
                )}

                <div className="friterie-burger-footer">
                  {mode === 'edit' ? (
                    <input
                      type="text"
                      className="admin-inline-input admin-inline-input--price"
                      value={burgerDuMois.price}
                      onChange={(e) =>
                        onChangeBurger?.('price', e.target.value)
                      }
                    />
                  ) : (
                    <p className="friterie-burger-price">
                      {burgerDuMois.price}
                    </p>
                  )}

                  {mode === 'view' && (
                    <button
                      type="button"
                      className="friterie-add-button friterie-add-button--primary"
                      disabled={!effectivelyOpen}
                      onClick={() =>
                        setModalItem({
                          item: {
                            name: burgerDuMois.title,
                            price: burgerDuMois.price,
                          },
                          sectionId: 'burger-du-mois',
                          gIndex: 0,
                          iIndex: 0,
                          showSauce: true,
                          showSupplements: true,
                          showViande: false,
                          displayName: burgerDuMois.title,
                          sauces: sauceList,
                          viandes: viandeList,
                        })
                      }
                    >
                      Ajouter au panier
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* ADMIN – ÉDITEUR ANNONCE PONCTUELLE */}
        {mode === 'edit' && (
          <div className="admin-announcement-panel">
            <p className="admin-announcement-title">📢 Annonce ponctuelle</p>
            <div className="admin-announcement-row">
              <label className="admin-announcement-toggle">
                <input
                  type="checkbox"
                  checked={!!(announcement?.active)}
                  onChange={(e) => onChangeAnnouncement?.('active', e.target.checked)}
                />
                Afficher l'annonce sur le menu
              </label>
            </div>
            <div className="admin-announcement-row">
              <label className="admin-announcement-toggle">
                <input
                  type="checkbox"
                  checked={!!announcement?.heroClosedBadgeActive}
                  onChange={(e) =>
                    onChangeAnnouncement?.('heroClosedBadgeActive', e.target.checked)
                  }
                />
                Afficher le badge "Fermé" sur le hero
              </label>
            </div>
            <textarea
              className="admin-announcement-textarea"
              rows={3}
              placeholder="Ex : Absente du 15 au 22 juillet pour cause de vacances. Réouverture le 23 juillet !"
              value={announcement?.message || ''}
              onChange={(e) => onChangeAnnouncement?.('message', e.target.value)}
            />
          </div>
        )}

        {/* ADMIN – JOURS D'OUVERTURE */}
        {mode === 'edit' && (
          <div className="admin-announcement-panel">
            <p className="admin-announcement-title">📅 Jours d'ouverture</p>
            <p className="admin-open-days-hint">
              Tu as le pouvoir de tout changer ! TOUT !
            </p>
            <button
              className="admin-button"
              onClick={() =>
                applyToAllDays({
                  open: "17:30",
                  close: "21:00"
                })
              }
            >
              Appliquer 17h30–21h à tous les jours
            </button>
            <div className="admin-open-days-grid">
              {DAY_NAMES.map((name, dayIndex) => {
                const todayOpening = openingHours?.[dayIndex] || {}
                const todayOnline = onlineOrderHours?.[dayIndex] || {}
                const isOpenDay = effectiveOpenDays.includes(dayIndex)
                const isOrderDay = effectiveOnlineOrderDays.includes(dayIndex)
                return (
                  <div key={dayIndex} className="admin-open-days-col">
                    <span className="admin-open-days-day-name">{name}</span>
                    <label className="admin-announcement-toggle">
                      <input
                        type="checkbox"
                        checked={isOpenDay}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...effectiveOpenDays, dayIndex].sort((a, b) => a - b)
                            : effectiveOpenDays.filter((d) => d !== dayIndex)
                          onChangeOpenDays?.('openDays', next)
                          // If unchecking an open day that was also an online-order day, remove it from online orders too
                          if (!next.includes(dayIndex) && effectiveOnlineOrderDays.includes(dayIndex)) {
                            onChangeOpenDays?.('onlineOrderDays', effectiveOnlineOrderDays.filter((d) => d !== dayIndex))
                          }
                        }}
                      />
                      Ouvert
                    </label>
                    <label className="admin-announcement-toggle">
                      <input
                        type="checkbox"
                        checked={isOrderDay}
                        disabled={!isOpenDay}
                        onChange={(e) => {
                          const next = e.target.checked
                            ? [...effectiveOnlineOrderDays, dayIndex].sort((a, b) => a - b)
                            : effectiveOnlineOrderDays.filter((d) => d !== dayIndex)
                          onChangeOpenDays?.('onlineOrderDays', next)
                        }}
                      />
                      Commandes en ligne
                    </label>
                    <div className="admin-hours-block">

                      <label>
                        Ouverture
                        <input
                          type="time"
                          value={todayOpening.open || ''}
                          onChange={(e) => {
                            const next = {
                              ...openingHours,
                              [dayIndex]: {
                                ...todayOpening,
                                open: e.target.value
                              }
                            }
                            onChangeOpenDays?.('openingHours', next)
                          }}
                        />
                      </label>

                      <label>
                        Fermeture
                        <input
                          type="time"
                          value={todayOpening.close || ''}
                          onChange={(e) => {
                            const next = {
                              ...openingHours,
                              [dayIndex]: {
                                ...todayOpening,
                                close: e.target.value
                              }
                            }
                            onChangeOpenDays?.('openingHours', next)
                          }}
                        />
                      </label>

                      <label>
                        Dernière commande
                        <input
                          type="time"
                          value={todayOnline.lastOrder || ''}
                          onChange={(e) => {
                            const next = {
                              ...onlineOrderHours,
                              [dayIndex]: {
                                ...todayOnline,
                                lastOrder: e.target.value
                              }
                            }
                            onChangeOpenDays?.('onlineOrderHours', next)
                          }}
                        />
                      </label>

                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* TABS + contenu */}
        <section className="friterie-tabs-layout">
          <section className="friterie-tabs-main">
            <nav className="friterie-tabs-nav" aria-label="Catégories de la carte">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={
                    'friterie-tab-button ' +
                    (tab.id === currentTab ? 'friterie-tab-button--active' : '')
                  }
                  onClick={() => setActiveTab(tab.id)}
                >
                  <span className="friterie-tab-label">{tab.label}</span>
                </button>
              ))}
            </nav>

            <div className="friterie-tab-panel">
              {activeSection ? (
                <section
                  className="friterie-tab-section"
                  aria-label={activeSection.label}
                >
                  <header className="friterie-tab-header">
                    {mode === 'edit' ? (
                      <input
                        type="text"
                        className="admin-input-title"
                        value={activeSection.label}
                        onChange={(e) => {
                          const idx = sections.findIndex(
                            (s) => s.id === activeSection.id
                          )
                          onChangeSectionField?.(idx, 'label', e.target.value)
                        }}
                      />
                    ) : (
                      <h3 className="friterie-tab-section-title">
                        {activeSection.label}
                      </h3>
                    )}

                    {activeSection.subtitle &&
                      (mode === 'edit' ? (
                        <textarea
                          rows={2}
                          className="admin-fullwidth"
                          value={activeSection.subtitle}
                          onChange={(e) => {
                            const idx = sections.findIndex(
                              (s) => s.id === activeSection.id
                            )
                            onChangeSectionField?.(
                              idx,
                              'subtitle',
                              e.target.value
                            )
                          }}
                        />
                      ) : (
                        <p className="friterie-tab-subtitle">
                          {activeSection.subtitle}
                        </p>
                      ))}
                  </header>

                  {activeSection.groups?.map((group, gIndex) => (
                    <div key={gIndex} className="friterie-group">
                      {group.title && (
                        <h4 className="friterie-subsection-title">
                          {group.title}
                        </h4>
                      )}

                      {/* Card display (burgers spéciaux, assiettes) */}
                      {mode === 'view' && group.displayMode === 'card' ? (
                        <CardGroup
                          group={group}
                          sectionId={activeSection.id}
                          gIndex={gIndex}
                          onAdd={handleAddItem}
                          isOpen={effectivelyOpen}
                        />
                      ) : (
                        <ul className="friterie-list">
                          {group.items?.map((item, iIndex) => {
                            const sectionIdx = sections.findIndex((s) => s.id === activeSection.id)
                            const isInlineSauce =
                              mode === 'view' &&
                              item.chooseSauce &&
                              activeSection.id === 'frites'
                            const isInlineFritesFormat =
                              mode === 'view' &&
                              activeSection.id === 'frites' &&
                              !item.chooseSauce
                            const inlineSauceKey = `${activeSection.id}-${gIndex}-${iIndex}`
                            const inlineFritesFormatKey = `${activeSection.id}-${gIndex}-${iIndex}`
                            const itemKey = `${sectionIdx}-${gIndex}-${iIndex}`
                            const isEditingThisItem = mode === 'edit' && editingItemKey === itemKey
                            // Sections where isMeatOption is relevant
                            const isMeatSection = MEAT_OPTION_SECTIONS.includes(activeSection.id)

                            return (
                              <li
                                key={iIndex}
                                className={`friterie-list-item${(isInlineSauce || isInlineFritesFormat) ? ' friterie-list-item--inline-choice' : ''}${item.outOfStock ? ' friterie-list-item--out-of-stock' : ''}`}
                              >
                                {mode === 'edit' ? (
                                  isEditingThisItem && editItemData ? (
                                    /* ── ITEM EDIT FORM ───────────────────────── */
                                    <div className="admin-item-edit-form">
                                      <div className="admin-add-item-row">
                                        <input
                                          type="text"
                                          className="admin-inline-input admin-inline-input--name"
                                          placeholder="Nom du produit *"
                                          value={editItemData.name}
                                          onChange={(e) => setEditItemData((prev) => ({ ...prev, name: e.target.value }))}
                                        />
                                        <input
                                          type="text"
                                          className="admin-inline-input admin-inline-input--price"
                                          placeholder="Prix (ex: 3,50€)"
                                          value={editItemData.price || ''}
                                          onChange={(e) => setEditItemData((prev) => ({ ...prev, price: e.target.value }))}
                                        />
                                      </div>
                                      <div className="admin-add-item-row">
                                        <input
                                          type="text"
                                          className="admin-inline-input admin-inline-input--subtitle"
                                          placeholder="Description (optionnel)"
                                          value={editItemData.subtitle || ''}
                                          onChange={(e) => setEditItemData((prev) => ({ ...prev, subtitle: e.target.value }))}
                                        />
                                      </div>
                                      <div className="admin-add-item-options">
                                        <label>
                                          <input type="checkbox" checked={!!editItemData.chooseMeat} onChange={(e) => setEditItemData((prev) => ({ ...prev, chooseMeat: e.target.checked }))} />
                                          {' '}Choix de la viande
                                        </label>
                                        <label>
                                          <input type="checkbox" checked={!!editItemData.chooseSauce} onChange={(e) => setEditItemData((prev) => ({ ...prev, chooseSauce: e.target.checked }))} />
                                          {' '}Choix de la sauce
                                        </label>
                                        <label>
                                          <input type="checkbox" checked={!!editItemData.chooseSupplements} onChange={(e) => setEditItemData((prev) => ({ ...prev, chooseSupplements: e.target.checked }))} />
                                          {' '}Suppléments
                                        </label>
                                        <label>
                                          <input type="checkbox" checked={!!editItemData.chooseBrochette} onChange={(e) => setEditItemData((prev) => ({ ...prev, chooseBrochette: e.target.checked }))} />
                                          {' '}Choix des brochettes
                                        </label>
                                        {isMeatSection && (
                                          <label>
                                            <input type="checkbox" checked={!!editItemData.isMeatOption} onChange={(e) => setEditItemData((prev) => ({ ...prev, isMeatOption: e.target.checked }))} />
                                            {' '}Viande au choix ?
                                          </label>
                                        )}
                                        <label className="admin-option-label--danger">
                                          <input type="checkbox" checked={!!editItemData.outOfStock} onChange={(e) => setEditItemData((prev) => ({ ...prev, outOfStock: e.target.checked }))} />
                                          {' '}Rupture de stock
                                        </label>
                                      </div>
                                      {group.displayMode === 'card' && (
                                        <div className="admin-add-item-image">
                                          {editItemData.image && !editItemData.image.includes('placeholder') && (
                                            <img src={resolveImageUrl(editItemData.image)} alt="Aperçu" className="admin-image-preview" />
                                          )}
                                          <button
                                            type="button"
                                            className="admin-button admin-button--primary admin-button--sm"
                                            onClick={() => {
                                              setPendingItemUpload({ sectionIndex: sectionIdx, groupIndex: gIndex, itemIndex: iIndex })
                                              cardItemFileInputRef.current?.click()
                                            }}
                                          >
                                            📷 {editItemData.image ? `Changer l'image` : `Ajouter une image`}
                                          </button>
                                          {cardItemUploadStatus[itemKey] && (
                                            <span className="admin-upload-status">{cardItemUploadStatus[itemKey]}</span>
                                          )}
                                        </div>
                                      )}
                                      <div className="admin-add-item-actions">
                                        <button
                                          type="button"
                                          className="admin-button admin-button--primary"
                                          disabled={!editItemData.name.trim()}
                                          onClick={() => handleEditItemConfirm(sectionIdx, gIndex, iIndex)}
                                        >✓ Valider</button>
                                        <button
                                          type="button"
                                          className="admin-button"
                                          onClick={handleEditItemCancel}
                                        >Annuler</button>
                                      </div>
                                    </div>
                                  ) : (
                                    /* ── COLLAPSED ITEM (edit mode, not editing) ── */
                                    <div className="admin-item-collapsed">
                                      <div className="friterie-list-main">
                                        <span className="friterie-item-name">{item.name}</span>
                                        {item.outOfStock && <span className="admin-out-of-stock-tag">Rupture</span>}
                                        <span className="friterie-item-dots" />
                                        {item.price && (
                                          <span className="friterie-item-price">{item.price}</span>
                                        )}
                                      </div>
                                      <div className="admin-item-actions">
                                        <button
                                          type="button"
                                          className="admin-button admin-button--sm"
                                          onClick={() => startEditingItem(sectionIdx, gIndex, iIndex, item)}
                                        >✏️ Modifier</button>
                                        <button
                                          type="button"
                                          className="admin-button admin-button--danger admin-button--sm"
                                          onClick={() => onDeleteItem?.(sectionIdx, gIndex, iIndex)}
                                        >🗑️ Supprimer</button>
                                      </div>
                                      {item.subtitle && (
                                        <p className="friterie-note admin-item-collapsed-note">{item.subtitle}</p>
                                      )}
                                    </div>
                                  )
                                ) : (
                                  /* ── VIEW MODE ────────────────────────────── */
                                  <>
                                    <div className="friterie-list-main">
                                      <span className="friterie-item-name">
                                        {item.name}
                                        {item.subtitle && (isInlineSauce || isInlineFritesFormat) && (
                                          <span
                                            className="friterie-item-info"
                                            data-tooltip={item.subtitle}
                                            aria-label={item.subtitle}
                                            tabIndex={0}
                                            role="note"
                                          >ℹ</span>
                                        )}
                                      </span>
                                      <span className="friterie-item-dots" />
                                      {item.price && (
                                        <span className="friterie-item-price">
                                          {item.price}
                                        </span>
                                      )}
                                    </div>

                                    {item.outOfStock ? (
                                      <span className="friterie-out-of-stock-badge">Rupture de stock</span>
                                    ) : isInlineFritesFormat ? (
                                      <div className="friterie-sauce-inline friterie-frites-format-inline">
                                        <div className="friterie-frites-format-toggle">
                                          {['en cornet', 'en ravier'].map((format) => (
                                            <button
                                              key={format}
                                              type="button"
                                              className={`friterie-frites-format-btn${inlineFritesFormats[inlineFritesFormatKey] === format ? ' friterie-frites-format-btn--active' : ''}`}
                                              onClick={() =>
                                                setInlineFritesFormats((prev) => ({
                                                  ...prev,
                                                  [inlineFritesFormatKey]: format,
                                                }))
                                              }
                                            >
                                              {format === 'en cornet' ? '🍟 En cornet' : '📦 En ravier'}
                                            </button>
                                          ))}
                                        </div>
                                        <span
                                          className="friterie-btn-wrapper"
                                          data-tooltip={!inlineFritesFormats[inlineFritesFormatKey] ? 'Veuillez choisir un format' : undefined}
                                        >
                                          <button
                                            type="button"
                                            className="friterie-add-button friterie-add-button--primary"
                                            disabled={!effectivelyOpen || !inlineFritesFormats[inlineFritesFormatKey]}
                                            onClick={() =>
                                              handleInlineFritesFormatAdd(item, activeSection.id, gIndex, iIndex)
                                            }
                                          >
                                            Ajouter
                                          </button>
                                        </span>
                                      </div>
                                    ) : isInlineSauce ? (
                                      <div className="friterie-sauce-inline">
                                        <select
                                          className="friterie-sauce-select"
                                          value={inlineSauces[inlineSauceKey] || ''}
                                          onChange={(e) =>
                                            setInlineSauces((prev) => ({
                                              ...prev,
                                              [inlineSauceKey]: e.target.value,
                                            }))
                                          }
                                        >
                                          <option value="">— Au choix en caisse —</option>
                                          {sauceList.map((s) => (
                                            <option key={s} value={s}>{s}</option>
                                          ))}
                                        </select>
                                        <button
                                          type="button"
                                          className="friterie-add-button friterie-add-button--primary"
                                          disabled={!effectivelyOpen}
                                          onClick={() =>
                                            handleInlineSauceAdd(item, activeSection.id, gIndex, iIndex)
                                          }
                                        >
                                          Ajouter
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        className="friterie-add-button"
                                        disabled={!effectivelyOpen}
                                        onClick={() =>
                                          handleAddItem(item, activeSection.id, gIndex, iIndex)
                                        }
                                      >
                                        Ajouter
                                      </button>
                                    )}

                                    {item.subtitle && !isInlineSauce && !isInlineFritesFormat && (
                                      <p className="friterie-note">
                                        {item.subtitle}
                                      </p>
                                    )}
                                  </>
                                )}

                                {/* Sauce list editor – only in the frites section */}
                                {mode === 'edit' && item.chooseSauce && activeSection.id === 'frites' && (
                                  <div className="admin-sauce-list-editor">
                                    <p className="admin-sauce-list-title">Sauces disponibles</p>
                                    {sauceList.map((sauce, sIdx) => (
                                      <div key={sIdx} className="admin-sauce-row">
                                        {editingSauceIdx === sIdx ? (
                                          <input
                                            type="text"
                                            className="admin-inline-input admin-inline-input--name"
                                            value={sauce}
                                            autoFocus
                                            onChange={(e) => {
                                              const updated = [...sauceList]
                                              updated[sIdx] = e.target.value
                                              onChangeSauceList?.(updated)
                                            }}
                                            onBlur={() => setEditingSauceIdx(null)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') setEditingSauceIdx(null) }}
                                          />
                                        ) : (
                                          <span className="admin-sauce-name">{sauce}</span>
                                        )}
                                        <button
                                          type="button"
                                          className="admin-button admin-button--sm"
                                          title="Modifier"
                                          onClick={() => setEditingSauceIdx(sIdx)}
                                        >✏️</button>
                                        <button
                                          type="button"
                                          className="admin-button admin-button--danger admin-button--sm"
                                          title="Supprimer"
                                          onClick={() => {
                                            onChangeSauceList?.(sauceList.filter((_, i) => i !== sIdx))
                                            if (editingSauceIdx === sIdx) setEditingSauceIdx(null)
                                          }}
                                        >🗑️</button>
                                      </div>
                                    ))}
                                    <div className="admin-sauce-add-row">
                                      <input
                                        type="text"
                                        className="admin-inline-input admin-inline-input--name"
                                        placeholder="Nouvelle sauce…"
                                        value={newSauceName}
                                        onChange={(e) => setNewSauceName(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && newSauceName.trim()) {
                                            onChangeSauceList?.([...sauceList, newSauceName.trim()])
                                            setNewSauceName('')
                                          }
                                        }}
                                      />
                                      <button
                                        type="button"
                                        className="admin-button admin-button--primary admin-button--sm"
                                        disabled={!newSauceName.trim()}
                                        onClick={() => {
                                          if (newSauceName.trim()) {
                                            onChangeSauceList?.([...sauceList, newSauceName.trim()])
                                            setNewSauceName('')
                                          }
                                        }}
                                      >➕ Ajouter</button>
                                    </div>
                                  </div>
                                )}
                              </li>
                            )
                          })}
                        </ul>
                      )}

                      {/* Add item form / button */}
                      {mode === 'edit' && (() => {
                        const sectionIdx = sections.findIndex((s) => s.id === activeSection.id)
                        const isAdding = addingInGroup?.sectionIndex === sectionIdx && addingInGroup?.groupIndex === gIndex
                        return isAdding ? (
                          <div className="admin-add-item-form">
                            <input ref={addItemFileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAddItemImageUpload} />
                            <div className="admin-add-item-row">
                              <input
                                type="text"
                                className="admin-inline-input admin-inline-input--name"
                                placeholder="Nom du produit *"
                                value={newItemData.name}
                                onChange={(e) => setNewItemData((prev) => ({ ...prev, name: e.target.value }))}
                              />
                              <input
                                type="text"
                                className="admin-inline-input admin-inline-input--price"
                                placeholder="Prix (ex: 3,50€)"
                                value={newItemData.price}
                                onChange={(e) => setNewItemData((prev) => ({ ...prev, price: e.target.value }))}
                              />
                            </div>
                            <div className="admin-add-item-row">
                              <input
                                type="text"
                                className="admin-inline-input admin-inline-input--subtitle"
                                placeholder="Description (optionnel)"
                                value={newItemData.subtitle}
                                onChange={(e) => setNewItemData((prev) => ({ ...prev, subtitle: e.target.value }))}
                              />
                            </div>
                            <div className="admin-add-item-options">
                              <label>
                                <input type="checkbox" checked={newItemData.chooseMeat} onChange={(e) => setNewItemData((prev) => ({ ...prev, chooseMeat: e.target.checked }))} />
                                {' '}Choix de la viande
                              </label>
                              <label>
                                <input type="checkbox" checked={newItemData.chooseSauce} onChange={(e) => setNewItemData((prev) => ({ ...prev, chooseSauce: e.target.checked }))} />
                                {' '}Choix de la sauce
                              </label>
                              <label>
                                <input type="checkbox" checked={newItemData.chooseSupplements} onChange={(e) => setNewItemData((prev) => ({ ...prev, chooseSupplements: e.target.checked }))} />
                                {' '}Suppléments
                              </label>
                              <label>
                                <input type="checkbox" checked={!!newItemData.chooseBrochette} onChange={(e) => setNewItemData((prev) => ({ ...prev, chooseBrochette: e.target.checked }))} />
                                {' '}Choix des brochettes
                              </label>
                              {MEAT_OPTION_SECTIONS.includes(activeSection.id) && (
                                <label>
                                  <input type="checkbox" checked={!!newItemData.isMeatOption} onChange={(e) => setNewItemData((prev) => ({ ...prev, isMeatOption: e.target.checked }))} />
                                  {' '}Nouvelle viande ?
                                </label>
                              )}
                              <label className="admin-option-label--danger">
                                <input type="checkbox" checked={!!newItemData.outOfStock} onChange={(e) => setNewItemData((prev) => ({ ...prev, outOfStock: e.target.checked }))} />
                                {' '}Rupture de stock
                              </label>
                            </div>
                            {group.displayMode === 'card' && (
                              <div className="admin-add-item-image">
                                {newItemData.image && (
                                  <img src={resolveImageUrl(newItemData.image)} alt="Aperçu" className="admin-image-preview" />
                                )}
                                <button
                                  type="button"
                                  className="admin-button admin-button--primary admin-button--sm"
                                  onClick={() => addItemFileInputRef.current?.click()}
                                >
                                  📷 {newItemData.image ? `Changer l'image` : `Ajouter une image`}
                                </button>
                                {addItemImageStatus && <span className="admin-upload-status">{addItemImageStatus}</span>}
                              </div>
                            )}
                            <div className="admin-add-item-actions">
                              <button
                                type="button"
                                className="admin-button admin-button--primary"
                                disabled={!newItemData.name.trim()}
                                onClick={() => {
                                  const newItem = { name: newItemData.name.trim(), price: newItemData.price.trim() }
                                  if (newItemData.subtitle.trim()) newItem.subtitle = newItemData.subtitle.trim()
                                  if (newItemData.chooseMeat) newItem.chooseMeat = true
                                  if (newItemData.chooseSauce) newItem.chooseSauce = true
                                  if (newItemData.chooseSupplements) newItem.chooseSupplements = true
                                  if (newItemData.chooseBrochette) newItem.chooseBrochette = true
                                  if (newItemData.isMeatOption) newItem.isMeatOption = true
                                  if (newItemData.outOfStock) newItem.outOfStock = true
                                  if (newItemData.image) newItem.image = newItemData.image
                                  onAddItem?.(sectionIdx, gIndex, newItem)
                                  setAddingInGroup(null)
                                  setNewItemData({ ...EMPTY_NEW_ITEM })
                                  setAddItemImageStatus('')
                                }}
                              >✓ Valider</button>
                              <button
                                type="button"
                                className="admin-button"
                                onClick={() => {
                                  setAddingInGroup(null)
                                  setNewItemData({ ...EMPTY_NEW_ITEM })
                                  setAddItemImageStatus('')
                                }}
                              >Annuler</button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="admin-button admin-button--primary admin-button--sm admin-add-item-btn"
                            onClick={() => {
                              setAddingInGroup({ sectionIndex: sectionIdx, groupIndex: gIndex })
                              setNewItemData({ ...EMPTY_NEW_ITEM, ...getGroupItemDefaults(group, activeSection.id) })
                              setAddItemImageStatus('')
                            }}
                          >➕ Ajouter un produit</button>
                        )
                      })()}
                    </div>
                  ))}
                </section>
              ) : (
                <p>Aucune section sélectionnée.</p>
              )}
            </div>
          </section>
        </section>
      </main>

      {/* MODAL sauce / suppléments */}
      {modalItem && (
        <ItemOptionsModal
          item={modalItem.item}
          showSauce={modalItem.showSauce}
          showSupplements={modalItem.showSupplements}
          showViande={modalItem.showViande}
          showBrochette={modalItem.showBrochette}
          sauces={modalItem.sauces}
          viandes={modalItem.viandes}
          brochettes={modalItem.brochettes}
          onConfirm={handleModalConfirm}
          onClose={() => setModalItem(null)}
        />
      )}
    </section>
  )
}

export default MenuDisplay
